import { useState } from "react";

const ACCENT = "#00e5b0";
const BG = "#080d17";
const CARD = "#0e1525";
const TEXT = "#edf2ff";
const TEXT2 = "#8fa3c8";
const TEXT3 = "#4d6080";
const BORDER = "rgba(255,255,255,0.06)";

export default function Landing({ onLogin, onRegister }) {
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const plans = [
    {
      name: "Free",
      price: { monthly: "P0", yearly: "P0" },
      description: "Get started with basic sports data tools",
      features: [
        "Real-time odds comparison",
        "Basic statistical analysis",
        "3 bookmaker comparisons",
        "Daily data updates",
        "Community access",
      ],
      cta: "Get Started",
      highlight: false,
    },
    {
      name: "Edge",
      price: { monthly: "P99/mo", yearly: "P890/yr" },
      description: "Advanced analytics for serious sports analysts",
      features: [
        "Everything in Free",
        "Real-time data across 6 platforms",
        "Advanced statistical models",
        "AI-powered combination analysis",
        "Automated alerts via Telegram",
        "Sharp money movement tracking",
        "Historical data access",
        "Priority support",
      ],
      cta: "Start Edge Plan",
      highlight: true,
    },
    {
      name: "Elite",
      price: { monthly: "P199/mo", yearly: "P1,790/yr" },
      description: "Professional-grade analytics suite",
      features: [
        "Everything in Edge",
        "Poisson statistical modeling",
        "Team form analysis",
        "Portfolio optimization tools",
        "API access",
        "Custom alerts",
        "Dedicated support",
        "Early access to new features",
      ],
      cta: "Start Elite Plan",
      highlight: false,
    },
  ];

  const features = [
    {
      icon: "📊",
      title: "Real-Time Data Aggregation",
      desc: "Aggregate and compare sports data from 6 major platforms simultaneously with sub-second refresh rates.",
    },
    {
      icon: "🤖",
      title: "AI-Powered Analysis",
      desc: "Machine learning models analyze thousands of data points to identify statistical anomalies and opportunities.",
    },
    {
      icon: "📈",
      title: "Poisson Distribution Modeling",
      desc: "Professional-grade mathematical models calculate true statistical probabilities for sports events.",
    },
    {
      icon: "⚡",
      title: "Instant Alerts",
      desc: "Receive automated notifications via Telegram when our algorithms detect significant statistical deviations.",
    },
    {
      icon: "💰",
      title: "Portfolio Analytics",
      desc: "Apply Modern Portfolio Theory and Kelly Criterion to optimize your sports analytics portfolio.",
    },
    {
      icon: "🎯",
      title: "Multi-Platform Comparison",
      desc: "Compare data across Betway, Sunbet, PST Bets, Bet267, BBets and BetXplosion in one dashboard.",
    },
  ];

  const stats = [
    { value: "6", label: "Data Sources" },
    { value: "700+", label: "Events Tracked Daily" },
    { value: "Real-Time", label: "Data Updates" },
    { value: "AI", label: "Powered Analysis" },
  ];

  return (
    <div style={{ background: BG, minHeight: "100vh", fontFamily: "'DM Sans', Arial, sans-serif", color: TEXT }}>

      {/* Nav */}
      <nav style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${BORDER}`, position: "sticky", top: 0, background: BG, zIndex: 100 }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 20 }}>
          OddsEdge<span style={{ color: ACCENT }}>BW</span>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={onLogin}
            style={{ padding: "8px 20px", background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 8, color: TEXT2, cursor: "pointer", fontSize: 14 }}
          >
            Sign In
          </button>
          <button
            onClick={onRegister}
            style={{ padding: "8px 20px", background: ACCENT, border: "none", borderRadius: 8, color: "#080d17", cursor: "pointer", fontSize: 14, fontWeight: 700 }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "80px 24px 60px", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(0,229,176,0.1)", border: `1px solid rgba(0,229,176,0.2)`, borderRadius: 20, padding: "6px 16px", fontSize: 12, color: ACCENT, marginBottom: 24, fontWeight: 600, letterSpacing: 1 }}>
          BOTSWANA'S SPORTS DATA ANALYTICS PLATFORM
        </div>
        <h1 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 800, fontFamily: "'Syne', sans-serif", margin: "0 0 20px", lineHeight: 1.15 }}>
          Professional Sports Data<br />
          <span style={{ color: ACCENT }}>Analytics for Botswana</span>
        </h1>
        <p style={{ fontSize: 18, color: TEXT2, maxWidth: 600, margin: "0 auto 40px", lineHeight: 1.7 }}>
          Real-time statistical analysis, multi-platform data aggregation, and AI-powered insights across all major Botswana sports platforms.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={onRegister}
            style={{ padding: "14px 32px", background: ACCENT, border: "none", borderRadius: 10, color: "#080d17", cursor: "pointer", fontSize: 16, fontWeight: 700 }}
          >
            Start Free →
          </button>
          <button
            onClick={onLogin}
            style={{ padding: "14px 32px", background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 10, color: TEXT2, cursor: "pointer", fontSize: 16 }}
          >
            Sign In
          </button>
        </div>
      </section>

      {/* Stats */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 60px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, background: CARD, borderRadius: 16, padding: "32px 24px", border: `1px solid ${BORDER}` }}>
          {stats.map(s => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 28, color: ACCENT, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 13, color: TEXT3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 80px" }}>
        <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 32, textAlign: "center", marginBottom: 48 }}>
          Professional Analytics Tools
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
          {features.map(f => (
            <div key={f.title} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "24px 20px" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{f.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 80px" }}>
        <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 32, textAlign: "center", marginBottom: 16 }}>
          Simple, Transparent Pricing
        </h2>
        <p style={{ textAlign: "center", color: TEXT2, marginBottom: 32 }}>
          Choose the plan that fits your analytics needs
        </p>

        {/* Billing toggle */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", background: CARD, borderRadius: 10, padding: 4, border: `1px solid ${BORDER}` }}>
            <button
              onClick={() => setBillingPeriod("monthly")}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, background: billingPeriod === "monthly" ? ACCENT : "transparent", color: billingPeriod === "monthly" ? "#080d17" : TEXT2 }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod("yearly")}
              style={{ padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, background: billingPeriod === "yearly" ? ACCENT : "transparent", color: billingPeriod === "yearly" ? "#080d17" : TEXT2 }}
            >
              Yearly <span style={{ fontSize: 11, color: billingPeriod === "yearly" ? "#080d17" : ACCENT }}>Save 25%</span>
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
          {plans.map(plan => (
            <div key={plan.name} style={{
              background: plan.highlight ? `linear-gradient(135deg, rgba(0,229,176,0.08), rgba(0,229,176,0.02))` : CARD,
              border: `1px solid ${plan.highlight ? "rgba(0,229,176,0.3)" : BORDER}`,
              borderRadius: 16, padding: "32px 24px",
              position: "relative",
            }}>
              {plan.highlight && (
                <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: ACCENT, color: "#080d17", fontSize: 11, fontWeight: 700, padding: "4px 16px", borderRadius: 20, whiteSpace: "nowrap" }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 20, marginBottom: 4 }}>{plan.name}</div>
              <div style={{ fontSize: 13, color: TEXT2, marginBottom: 20 }}>{plan.description}</div>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 32, color: ACCENT, marginBottom: 24 }}>
                {plan.price[billingPeriod]}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 32px", display: "flex", flexDirection: "column", gap: 10 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ fontSize: 13, color: TEXT2, display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ color: ACCENT, flexShrink: 0 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={onRegister}
                style={{ width: "100%", padding: "12px", background: plan.highlight ? ACCENT : "transparent", border: `1px solid ${plan.highlight ? ACCENT : BORDER}`, borderRadius: 10, color: plan.highlight ? "#080d17" : TEXT2, cursor: "pointer", fontSize: 14, fontWeight: 700 }}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 80px" }}>
        <div style={{ background: `linear-gradient(135deg, rgba(0,229,176,0.08), rgba(0,229,176,0.02))`, border: `1px solid rgba(0,229,176,0.2)`, borderRadius: 20, padding: "60px 40px", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 32, marginBottom: 16 }}>
            Start Analysing Today
          </h2>
          <p style={{ color: TEXT2, fontSize: 16, marginBottom: 32, maxWidth: 500, margin: "0 auto 32px" }}>
            Join Botswana's most advanced sports data analytics platform. Free to start, no credit card required.
          </p>
          <button
            onClick={onRegister}
            style={{ padding: "14px 40px", background: ACCENT, border: "none", borderRadius: 10, color: "#080d17", cursor: "pointer", fontSize: 16, fontWeight: 700 }}
          >
            Get Started Free →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${BORDER}`, padding: "32px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
          OddsEdge<span style={{ color: ACCENT }}>BW</span>
        </div>
        <p style={{ color: TEXT3, fontSize: 13, marginBottom: 8 }}>
          Botswana's Sports Data Analytics Platform
        </p>
        <p style={{ color: TEXT3, fontSize: 12 }}>
          © 2026 OddsEdgeBW (Pty) Ltd · <a href="mailto:support@oddsedgebw.co.bw" style={{ color: TEXT3 }}>support@oddsedgebw.co.bw</a> · Gaborone, Botswana
        </p>
        <p style={{ color: TEXT3, fontSize: 11, marginTop: 8 }}>
          OddsEdgeBW provides sports data analytics tools for informational purposes only.
        </p>
      </footer>
    </div>
  );
}